import {NestedJobStop} from "../generated/graphql";
import {isCompleted} from "./stops/StopUtils";
import dayjs, {Dayjs} from "dayjs";

export const findEarliestJobStop = (stops: NestedJobStop[]): NestedJobStop | null => {
  const notCompletedStops = stops.filter((stop) => !isCompleted(stop));
  if (notCompletedStops.length === 0) {
    return null;
  }
  return notCompletedStops.reduce((earliest, current) => {
    const earliestJobStopTime = dayjs(earliest.scheduledDateTime);
    const currentJobStopTime = dayjs(current.scheduledDateTime);
    return currentJobStopTime.isBefore(earliestJobStopTime) ? current : earliest;
  }, notCompletedStops[0]);
};

export const findEarliestStartDate = (stops: NestedJobStop[]): Dayjs => {
  const currentDate = dayjs();
  const notCompletedStops = stops.filter((stop) => !isCompleted(stop));
  const earliestJobStop = findEarliestJobStop(notCompletedStops);
  if (!earliestJobStop) {
    return currentDate;
  }
  const rawDate = dayjs(earliestJobStop.scheduledDateTime);
  return rawDate.isAfter(currentDate) ? rawDate : currentDate;
};

export const combineDatesAndTime = (
  date: Dayjs | null | undefined,
  time: Dayjs | null | undefined,
  timeZone: string | null = null,
  keepLocalTime = false
): Dayjs => {
  if (!date || !time || !date.isValid() || !time.isValid()) {
    return dayjs("Invalid Date");
  }
  if (timeZone && timeZone !== Intl.DateTimeFormat().resolvedOptions().timeZone) {
    date = date.tz(timeZone, keepLocalTime);
    time = time.tz(timeZone, keepLocalTime);
  }

  return date.hour(time.hour()).minute(time.minute());
};

/**
 * Moves a start date/time from one time zone to another.
 * - Date only: keeps the calendar date shown in `fromTimeZone` (a date without a time is not an instant).
 * - Time only, or date + time: keeps the same instant.
 * Shared by the IP "Apply All" controls and the per-driver reducers so both behave identically.
 */
export const convertStartDateTimeToTimeZone = (
  startDate: Dayjs | null,
  startTime: Dayjs | null,
  fromTimeZone: string | null | undefined,
  timeZone: string
): {startDate: Dayjs | null; startTime: Dayjs | null} => {
  if (startDate && !startTime) {
    // Read the date as displayed in the previous zone first — the Dayjs object may carry another offset
    const displayedDate = fromTimeZone ? startDate.tz(fromTimeZone) : startDate;
    return {startDate: displayedDate.tz(timeZone, true), startTime};
  }
  if (!startDate && startTime?.isValid()) {
    return {startDate, startTime: startTime.tz(timeZone)};
  }
  if (startDate?.isValid() && startTime?.isValid()) {
    const combinedDateTime = combineDatesAndTime(startDate, startTime, timeZone);
    return {startDate: combinedDateTime, startTime: combinedDateTime};
  }
  return {startDate, startTime};
};

export const getTimeZoneOffset = (timeZone: string): number => {
  const date = dayjs().tz(timeZone);
  return date.utcOffset();
};

export const isStartLocationValid = (startLocation: string | null): boolean => {
  return !!startLocation && startLocation.trim().length > 0;
};

export const isFutureDateTime = (startDateTime: Dayjs | null | undefined): boolean => {
  if (!startDateTime?.isValid()) return false;
  const now = dayjs().startOf("minute");
  return !startDateTime.isBefore(now);
};

export const isFutureDate = (date: Dayjs | null | undefined): boolean => {
  if (!date?.isValid()) return false;
  return date.isAfter(dayjs().subtract(1, "day"), "day");
};
